import React from 'react';
import { Box } from '@mui/material';
import { useMutation } from 'react-query';
import axios, { endPoints } from 'api/request';
import throttle from 'lodash/throttle';
import { useRecoilState } from 'recoil';
import { OrderPayload, validOnboardingForm } from 'pages/project-onboarding/store';
import Select, { CssTextField } from 'components/select';

const SASTApplicationNameSelect = () => {
    const [isTouched, setIsTouched] = React.useState(false);
    const [order, setOrder] = useRecoilState(OrderPayload);
    const [isValidForm, setValidForm] = useRecoilState(validOnboardingForm);
    const [value, setValue] = React.useState(null);
    const [ccInputValue, setCCInputValue] = React.useState('-');
    const [options, setOptions] = React.useState([]);
    const [initOptions, setInitOptions] = React.useState([]);
    const flag = false;

    const sastApplicationsApi = useMutation(() =>
        axios.get(`${endPoints.day2Operation.ProjectOnboarding.Jwt.sastApplicationName}?projectName=${ccInputValue?.toUpperCase()}`)
    );
    const bAppPostApi = useMutation((payload) => axios.post(`${endPoints.day2Operation.ProjectOnboarding.businessApp}`, payload));

    const setBusinessApplication = (sastApplicationName, sastBusinessApplicationDetails) => {
        setOrder({
            ...order,
            sastApplicationName,
            sastBusinessApplicationDetails,
        });
        setValidForm({ ...isValidForm, isDuplicateSastApplication: false });
    };

    const resetOptions = () => {
        setOptions(initOptions);
        setBusinessApplication(null, null);
    };

    const fetch = React.useMemo(
        () =>
            throttle(() => {
                sastApplicationsApi?.mutate();
            }, 1000),
        []
    );

    const validateInput = () => {
        if (isTouched && order?.isTouched && !order?.sastApplicationName) {
            return 'validate';
        }
        return '';
    };

    React.useEffect(() => {
        if (ccInputValue === '') {
            resetOptions();
        }
        if (ccInputValue !== '' && ccInputValue.length > 2) fetch();
    }, [ccInputValue, fetch]);

    React.useEffect(() => {
        if (sastApplicationsApi?.isSuccess) {
            if (sastApplicationsApi?.data?.data?.projectNames) {
                const combinedValue = sastApplicationsApi?.data?.data?.projectNames.map((app) => ({
                    label: app,
                    value: app,
                    data: app,
                }));
                setOptions(combinedValue);
            }
        }
    }, [sastApplicationsApi?.isSuccess]);

    React.useEffect(() => {
        if (bAppPostApi?.isSuccess) {
            if (bAppPostApi?.data?.data?.applications) {
                const combinedValue = bAppPostApi?.data?.data?.applications.map((app) => ({
                    label: `${app.businessApplicationName}`,
                    value: app?.businessApplicationName,
                    data: app,
                }));
                if (order?.sastBusinessApplicationDetails) combinedValue.push(order?.sastBusinessApplicationDetails);
                setOptions(combinedValue);
                setInitOptions(combinedValue);
            }
        }
    }, [bAppPostApi?.isSuccess]);

    React.useEffect(() => {
        if (flag) {
            bAppPostApi?.mutate({
                pageOffset: 0,
                pageLimit: 20,
            });
        }
    }, [order?.environment]);

    const isExistsInOptions = (region) => {
        return options.find((option) => option?.label === region.label || option?.value === region);
    };

    React.useEffect(() => {
        if (value === null && initOptions?.length) {
            resetOptions();
        } else if (value) {
            setBusinessApplication(value?.label, value?.data);
        }
    }, [value]);

    React.useEffect(() => {
        if (options && options?.length === 1 && !order?.sastApplicationName) {
            setOrder({ ...order, sastApplicationName: options?.[0].label, sastBusinessApplicationDetails: options?.[0]?.data });
        }
        if (options && options?.length && order?.sastBusinessApplicationDetails && !order?.sastApplicationName) {
            const sastApplicationName = isExistsInOptions(order?.sastBusinessApplicationDetails);
            setOrder({ ...order, sastApplicationName: sastApplicationName?.label, sastBusinessApplicationDetails: sastApplicationName?.data });
        }
    }, [options]);

    React.useEffect(() => {
        if (!order?.sastApplicationName && order?.isTouched) setIsTouched(true);
        else setIsTouched(false);
    }, [order?.sastApplicationName, order?.isTouched]);

    return (
        <>
            <Box display="flex" alignItems="center" gap={1} data-testid="business-application-box">
                <Select
                    key="sastApplicationName"
                    data-testid="business-application-search"
                    size="small"
                    isLoading={sastApplicationsApi?.isLoading || bAppPostApi?.isLoading}
                    label="SAST Application Name *"
                    value={order?.sastApplicationName || null}
                    disabled={order?.environment === null}
                    onInputChange={(event, newInputValue) => {
                        setCCInputValue(newInputValue);
                    }}
                    onChange={(event, newValue) => {
                        if (typeof newValue === 'string') {
                            setValue({
                                label: newValue,
                                value: newValue,
                                data: null,
                            });
                        } else if (newValue && newValue.inputValue) {
                            // Create a new value from the user input
                            setValue({
                                label: newValue.inputValue,
                                value: newValue.inputValue,
                                data: null,
                            });
                        } else {
                            setValue(newValue);
                        }
                    }}
                    getOptionLabel={(option) => {
                        // Value selected with enter, right from the input
                        if (typeof option === 'string') {
                            return option;
                        }
                        // Add "xxx" option created dynamically
                        if (option.inputValue) {
                            return option.inputValue;
                        }
                        // Regular option
                        return option.label;
                    }}
                    filterOptions={(x) => x}
                    selectOnFocus
                    clearOnBlur
                    handleHomeEndKeys
                    id="free-solo-with-text-demo"
                    options={options}
                    freeSolo
                    renderInput={(params) => (
                        <CssTextField
                            // eslint-disable-next-line react/jsx-props-no-spreading
                            {...params}
                            label="SAST Application Name *"
                            sx={{
                                '& label.MuiInputLabel-shrink': {
                                    color: '#0047BA !important',
                                },
                            }}
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: <>{params.InputProps.endAdornment}</>,
                            }}
                            fullWidth
                        />
                    )}
                    className={validateInput()}
                />
            </Box>
        </>
    );
};

export default React.memo(SASTApplicationNameSelect);
